import React, { useEffect, useContext, useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

// Components
import InputPanel from '../../components/InputPanel/InputPanel';
import RateComparisonChart from '../../components/RateComparisonChart/RateComparisonChart';
import SelectCurrentRate from '../../components/InputComponents/SelectCurrentRate/SelectCurrentRate';
import UserPrefsContext from '../../../context/UserPrefs/UserPrefsContext';
import SelectRateComparisonVehicle from '../../components/InputComponents/SelectRateComparisonVehicle/SelectRateComparisonVehicle';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import SlideMilesDrivenAnnually from '../../../components/InputComponents/SlideMilesDrivenAnnually/SlideMilesDrivenAnnually';
import SlideElectricMilesPortionForPhev from '../../../components/InputComponents/SlideElectricMilesPortionForPhev/SlideElectricMilesPortionForPhev';
import SlideCurrentMonthlyBill from '../../components/InputComponents/SlideCurrentMonthlyBill/SlideCurrentMonthlyBill';
import SlideGasolinePrice from '../../../components/InputComponents/SlideGasolinePrice/SlideGasolinePrice';
import SlideEquivalentMilesPerGallon from '../../components/InputComponents/SlideEquivalentMilesPerGallon/SlideEquivalentMilesPerGallon';
import SlidePublicChargingPercentage from '../../components/InputComponents/SlidePublicChargingPercentage/SlidePublicChargingPercentage';
import SelectPublicChargingCost from '../../components/InputComponents/SelectPublicChargingCost/SelectPublicChargingCost';

// Data
import rates from '../../data/rates';
import chargingPatterns from '../../data/chargingPatterns';
import zipcodeToBaselineAllocationRegion from '../../data/zipcodeToBaselineAllocationRegion.json';

// Functions
import isPHEV from '../../../functions/vehicle/isPHEV';
import { FormatAsCents } from '../../../utils/Helpers/Format';
import getLoadProfileData from '../../data/loadProfiles/getLoadProfileData';
import totalElectricVehicleKwhPerYear from '../../functions/totalElectricVehicleKwhPerYear';
import RateCostCalculator from '../../functions/RateCostCalculator';
import GasolineCostCalculator from '../../functions/GasolineCostCalculator';
import calculateRateTotals from '../../functions/calculateRateTotals';
import rateComparisonChartData from '../../functions/rateComparisonChartData';

// Components
import RateComparisonBanner from '../../components/RateComparisonBanner/RateComparisonBanner';
import RateDetails from '../../components/RateDetails/RateDetails';
import RateOptionsWizard from '../../components/RateOptionsWizard/RateOptionsWizard';

// Styles
import './RateComparison.scss';

import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FaTimes } from 'react-icons/fa';
import USER_PREFS_PRESETS from '../../../context/UserPrefs/USER_PREF_PRESETS';

const RateComparison = ({ electricVehicles, ip, uuid }) => {
  // TODO: make a custom hook for this
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 720);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 720);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setIsMobileView(window.innerWidth < 720);
  }, []);

  const intl = useIntl();
  useEffect(() => {
    document.title =
      process.env.REACT_APP_PAGES_RATES_TITLE || 'SMUD Rate Advisor';
  });

  const userPrefs = useContext(UserPrefsContext);

  useEffect(() => {
    userPrefs.set(USER_PREFS_PRESETS);
    // eslint-disable-next-line
  }, []);

  const {
    isComparingLowestRateOnly,
    vehicleIdForRateComparison,
    chargingPatternId,
    currentRateId,
    zipcode,
    hasSolarAtHome,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    publicChargingPercentage,
    publicChargingCostInCentsPerKwh,
    currentMonthlyBill,
    gasolinePriceInCentsPerGal,
    equivalentMilesPerGallon,
    selectedEnergyCostIds,
    currentRateClimateZone,
  } = userPrefs.get();

  const [filtersIsOpen, setFiltersIsOpen] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState();
  const [bestSaving, setBestSaving] = useState();

  useEffect(() => {
    if (electricVehicles && vehicleIdForRateComparison) {
      setSelectedVehicle(
        electricVehicles.find(
          (ev) => ev.electric_vehicle_id === vehicleIdForRateComparison
        )
      );
    } else if (electricVehicles) {
      setSelectedVehicle(electricVehicles[0]);
    }
  }, [electricVehicles, vehicleIdForRateComparison]);

  const currentChargingPattern =
    chargingPatterns.find((p) => p.id === chargingPatternId) ||
    chargingPatterns[0];

  const currentTerritory = zipcodeToBaselineAllocationRegion[zipcode] || '8';

  const currentRate =
    Object.values(rates).find((e) => e.id === currentRateId) ||
    rates[currentRateId] ||
    rates[Object.keys(rates)[0]];
  const evaluatedCurrentRate = {
    ...currentRate,
    data: currentRate.data(currentTerritory, currentRateClimateZone),
  };

  const homeLoadProfile = getLoadProfileData({
    rateGroup: currentRateId,
    climateZone: currentRateClimateZone,
    hasSolar: hasSolarAtHome,
  });

  const totalElectricVehicleKwh = totalElectricVehicleKwhPerYear({
    kwhPer100Miles:
      selectedVehicle && selectedVehicle.electric_efficiency
        ? selectedVehicle.electric_efficiency
        : 0,
    milesDrivenAnnually,
    percentageDrivenElectric: isPHEV(selectedVehicle)
      ? electricMilesPortionForPhev
      : 100,
  });

  const homeChargingKwh =
    totalElectricVehicleKwh * ((100 - publicChargingPercentage) / 100);

  const publicChargingElectricCostPerMonth =
    (totalElectricVehicleKwh *
      (publicChargingCostInCentsPerKwh / 100) *
      (publicChargingPercentage / 100)) /
    12;

  const rateCostCalculator = new RateCostCalculator({
    typicalHomeLoadProfile: homeLoadProfile,
    currentRate: evaluatedCurrentRate,
    currentMonthlyBill,
    homeChargingKwh,
    chargingPatternLoadProfile: currentChargingPattern.loadProfile,
  });

  const gasolineCostCalculator = new GasolineCostCalculator({
    selectedVehicle,
    gasolinePriceInCentsPerGal,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    equivalentMilesPerGallon,
  });

  const evaluatedRates = Object.keys(rates).map((k) => ({
    ...rates[k],
    data: rates[k].data(currentTerritory, currentRateClimateZone),
  }));

  const rateTotals = calculateRateTotals({
    rates: evaluatedRates,
    currentRate: {
      ...currentRate,
      data: currentRate.data(currentTerritory, currentRateClimateZone),
    },
    rateCostCalculator,
    gasolineCostCalculator,
    publicChargingElectricCostPerMonth,
    selectedEnergyCostIds,
    isComparingLowestRateOnly,
  });

  // Constants for better maintainability
  const R14_RATE_ID = 'R14';
  const R14_CURRENT_RATE_ID = 'R14-current';
  const R14_EV_RATE_ID = 'R14EV';
  const TOD_RATE_ID = 'TOD';

  const translatedRates = useMemo(() => (
    Object.keys(rates).reduce((acc, key) => {
      const [translationKey, ...rest] = rates[key].title;
      acc[key] = {
        ...rates[key],
        title: [
          intl.formatMessage({
            id: translationKey,
            defaultMessage: 'Rate',
          }),
          ...rest,
        ]
      };
      return acc;
    }, {})
  ), [intl]);

  const rateTotalsWithEv = useMemo(() => {
    // Filter out TOD rates for R14, keep all rates for others
    const filteredRateTotals =
      currentRate.id !== R14_RATE_ID
        ? rateTotals
        : rateTotals.filter((rateItem) => rateItem.id !== TOD_RATE_ID);

    // Special handling for R14 rate to add electric vehicle variant
    if (currentRate.id === R14_RATE_ID) {
      const fixedRate = rateTotals.find(
        (rate) => rate.id === R14_CURRENT_RATE_ID
      );

      if (!fixedRate) {
        console.warn(`Fixed rate with id '${R14_CURRENT_RATE_ID}' not found`);
        return filteredRateTotals;
      }
      
      const currentRateTitle = intl.formatMessage({
        id: currentRate.title[0],
        defaultMessage: 'Rate',
      });

      const evRate = {
        ...fixedRate,
        id: R14_EV_RATE_ID,
        gas: 0,
        title: currentRateTitle,
        home: fixedRate.home,
        ev: fixedRate.evCostValue,
      };

      return [...filteredRateTotals, evRate];
    }

    return filteredRateTotals;
  }, [rateTotals, currentRate.id, currentRate.title, intl]);

  const labelHome = intl.formatMessage({
    id: 'savingsGraphHomeLabel',
    defaultMessage: 'Home usage',
  });
  const labelElectric = intl.formatMessage({
    id: 'savingsGraphEVLabel',
    defaultMessage: 'EV charging',
  });
  const labelGasoline = intl.formatMessage({
    id: 'savingsGraphGasLabel',
    defaultMessage: 'Gasoline',
  });

    const rateTotalsWithTranslatedTitles = useMemo(
      () =>
        rateTotalsWithEv.map((rate) => {
          const translated = Object.values(translatedRates).find(
            (r) => r.id === rate.id.replace('-current', '')
          );

          let title = translated ? translated.title : rate.title;

          if (rate.id.endsWith('-current')) {
            const currentLabel = intl.formatMessage({
              id: 'graph.current',
              defaultMessage: '(Current)',
            });
            title = [title, currentLabel]
          }

          return {
            ...rate,
            title,
            label: title,
          };
        }),
      [rateTotalsWithEv, translatedRates, intl]
    );

  const chartData = rateComparisonChartData({
    rateTotals: rateTotalsWithTranslatedTitles,
    labelHome,
    labelElectric,
    labelGasoline,
  });

  if (!electricVehicles || !selectedVehicle)
    return (
      <div className="text-center loading-spinner">
        <LoadingSpinner />
      </div>
    );

  const ratesOptions = Object.keys(translatedRates)
    .filter((rate) => rate !== 'DIS')
    .map((key) => translatedRates[key]);

  return (
    <section className="container RateAdvisor">
      <div className="row">
        <div className="col-sm-12 text-center">
          <h1 className="header">
            <FormattedMessage
              id="rateAdvisor.title"
              defaultMessage="Rate advisor"
              description="Rate advisor"
            />
          </h1>
          <h2 className="small-centered mb-bottom sub-header">
            <FormattedMessage
              id="rateAdvisor.sub"
              defaultMessage="See how much you can save with the EV rate credit. Adjust the settings below to compare savings."
              description="Rate Advisor Subtitle"
            />
          </h2>
        </div>
      </div>

      {!filtersIsOpen && isMobileView && (
        <button
          type="button"
          className="btn btn-smud-secondary adjustFilters"
          onClick={() => setFiltersIsOpen(!filtersIsOpen)}
        >
          <FormattedMessage
            id="filterClosed"
            defaultMessage="Adjust filters"
            description="Adjust filters"
          />
        </button>
      )}

      {isMobileView && filtersIsOpen && (
        <div className="mobile-header">
          <div className="mobile-header-title">
            <FormattedMessage
              id="rateAdvisorfilters"
              defaultMessage="Rate advisor filters"
              description="Rate advisor filters"
            />
          </div>

          <Button close onClick={() => setFiltersIsOpen(false)}>
            <FaTimes />
          </Button>
        </div>
      )}

      <div className="row">
        {((filtersIsOpen && isMobileView) || !isMobileView) && (
          <div className="col-md-3">
            <RateOptionsWizard
              electricVehicles={electricVehicles}
              rates={ratesOptions}
              rateTotals={rateTotals}
              selectedVehicle={selectedVehicle}
              utilityName="SCE"
              typicalDriverName="Californian"
              mostCommonRateName={
                translatedRates[Object.keys(translatedRates)[0]].title
              }
              bestSaving={bestSaving}
            />
            <InputPanel
              title={intl.formatMessage({
                id: 'basicFilters',
                defaultMessage: 'Basic filters',
              })}
              className="mb-4"
            >
              <SelectCurrentRate rates={ratesOptions} />
              <SelectRateComparisonVehicle
                electricVehicles={electricVehicles}
                selectedVehicle={selectedVehicle}
              />
              <SlideMilesDrivenAnnually />
            </InputPanel>

            <InputPanel
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'homeAssumptions',
                      defaultMessage: 'Home assumptions',
                    })
                  : 'Home assumptions'
              }
              className="mb-4"
              defaultIsCollapsed={false}
            >
              <SlideCurrentMonthlyBill id="monthly-slider-filters" />
            </InputPanel>

            <InputPanel
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'drivingAssumptions',
                      defaultMessage: 'Driving assumptions',
                    })
                  : 'Driving assumptions'
              }
              className="mb-4"
              defaultIsCollapsed={false}
            >
              {isPHEV(selectedVehicle) && (
                <SlideElectricMilesPortionForPhev
                  label={
                    intl.formatMessage
                      ? intl.formatMessage({
                          id: 'electricPortion',
                          defaultMessage: 'Electric Portion',
                        })
                      : 'Electric Portion'
                  }
                  description={(val) => (
                    <FormattedMessage
                      id="electricPortion.description"
                      defaultMessage="{electric}% electric / {gasoline}% gasoline"
                      values={{
                        electric: val,
                        gasoline: 100 - val,
                      }}
                    />
                  )}
                />
              )}
              <SlideGasolinePrice
                label={
                  intl.formatMessage
                    ? intl.formatMessage({
                        id: 'gasolinePrice',
                        defaultMessage: 'Gasoline price',
                      })
                    : 'Gasoline price'
                }
                description={(val) => (
                  <FormattedMessage
                    id="gasPrice.gallon"
                    defaultMessage="{value}/gallon"
                    values={{ value: FormatAsCents(val / 100) }}
                  />
                )}
              />
              <SlideEquivalentMilesPerGallon />
              <SlidePublicChargingPercentage />
              <SelectPublicChargingCost />
            </InputPanel>
          </div>
        )}

        {((!filtersIsOpen && isMobileView) || !isMobileView) && (
          <div className="col-md-9">
            <div className="rate-comparison-title">
              <h3>
                <FormattedMessage
                  id="rateAdvisor.rateComparison"
                  defaultMessage="Rate savings comparison"
                  description="Rate comparison title"
                />
              </h3>
            </div>
            <RateComparisonBanner
              rateTotals={rateTotals}
              selectedVehicle={selectedVehicle}
              bestSaving={bestSaving}
            />
            <RateComparisonChart
              title={
                intl.formatMessage
                  ? intl.formatMessage({
                      id: 'pricingPlan',
                      defaultMessage: 'Pricing Plan Comparison',
                    })
                  : 'Pricing Plan Comparison'
              }
              chartData={chartData}
              className="d-md-block"
              setBestSaving={setBestSaving}
            />
            <div className="mt-4">
              <RateDetails
                rates={Object.keys(translatedRates).map(
                  (k) => translatedRates[k]
                )}
                selectedRateDetailsId={userPrefs.get('selectedRateDetailsId')}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default RateComparison;

RateComparison.propTypes = {
  electricVehicles: PropTypes.array,
  ip: PropTypes.string,
  uuid: PropTypes.string,
};
